// Jihoz EGASI — "kim" degan savolga javob beradigan tanlagichlar uchun.
//
// <h2>Nega bu fayl kerak bo'ldi (XSB-003)</h2>
// Tashqi tekshiruvchi: «Jihoz kartochkasida Egasi "tayinlanmagan" ko'rinadi,
// lekin tahrirlash oynasida Egasi maydoni YO'Q — foydalanuvchi egani tayinlay
// ham, o'zgartira ham olmaydi».
//
// ⚠️ BACKEND BUNI ALLAQACHON QO'LLAB-QUVVATLAYDI:
//   POST /api/inventory/equipment/{id}/assign   {ownerType, ownerId}
//   POST /api/inventory/equipment/{id}/unassign
// va `ownerType` u yerda ERKIN SATR EMAS — yopiq ro'yxat
// (`inventory/equipment/domain/OwnerType.java`): SUBSCRIBER, AGENT, VENUE,
// OTHER. Ya'ni bu funksiyaning yo'qligi backend kamchiligi emas edi, faqat
// panelda tanlagich yozilmagan edi.
//
// <h2>⚠️ Nega "id ni qo'lda yozing" YETARLI EMAS</h2>
// `equipmentApi.assignEquipment` javadoc'ida yozilgan va u hamon to'g'ri:
// xom UUID maydoni yaroqsiz biriktirishlarni ochib berardi — jihoz mavjud
// bo'lmagan egaga bog'langan holda `ASSIGNED` bo'lib qolardi va inventar
// hisobi o'z-o'zidan yolg'onga aylanardi. Shuning uchun bu yerda HAR TUR
// UCHUN HAQIQIY RO'YXAT so'raladi va operator NOMDAN tanlaydi.
//
// `OTHER` da esa ro'yxat yo'q — u ataylab "kelajakda ma'no aniqlashtiriladi"
// degan tur. Shuning uchun u yerda UUID qo'lda kiritiladi, LEKIN shakli
// tekshiriladi va bu holat ekranda ochiq aytiladi.
//
// <h2>⚠️ Nega `features/subscriber`, `features/agent`, `features/venue` dan
// import QILINMAYDI</h2>
// `docs/07-FRONTEND.md` §6: "features/* bir-birini import qilmaydi". Shuning
// uchun bu yerda o'z, ATAYLAB TOR sxemalar bor — faqat `id` va ko'rsatiladigan
// nom. Bu nusxa emas, boshqa (kichikroq) kontrakt: to'liq shakl o'z
// feature'ida qoladi va u o'zgarganda bu yerdagi ikki maydon buzilmaydi.
//
// ⚠️ `shared/api/reference.ts` ga ham qo'yilmadi: u yerda BIR NECHTA
// feature'ga kerak bo'ladigan ro'yxatlar yashaydi, bu esa faqat inventarga
// kerak. Ikkinchi iste'molchi paydo bo'lsa — o'sha yerga ko'chiriladi.

import { useMemo } from 'react'
import { useQuery } from '@tanstack/react-query'
import { z } from 'zod'
import { api } from '@/shared/api/client'
import { queryKeys } from '@/shared/api/queryKeys'
import { uuidSchema } from '@/shared/api/schemas'
import { serverPageSchema } from '@/shared/api/serverPage'
import type { Permission } from '@/shared/auth/permissions'
import { usePermissions } from '@/shared/auth/usePermissions'

/**
 * Backenddagi `OwnerType` enum'ining AYNAN o'zi.
 *
 * ⚠️ Tartib ekranda ko'rinadigan tartib: eng ko'p uchraydigani birinchi.
 * `OTHER` oxirida — u istisno, standart tanlov emas.
 */
export const EQUIPMENT_OWNER_TYPES = ['SUBSCRIBER', 'AGENT', 'VENUE', 'OTHER'] as const
export type EquipmentOwnerType = (typeof EQUIPMENT_OWNER_TYPES)[number]

/** Tanlash ro'yxati bor turlar — `OTHER` ni biriktirishda tanlab bo'lmaydi */
export const ASSIGNABLE_OWNER_TYPES = EQUIPMENT_OWNER_TYPES.filter(
  (type): type is Exclude<EquipmentOwnerType, 'OTHER'> => type !== 'OTHER',
)
type AssignableOwnerType = (typeof ASSIGNABLE_OWNER_TYPES)[number]

export function isAssignableOwnerType(value: string | null): value is AssignableOwnerType {
  return value !== null && (ASSIGNABLE_OWNER_TYPES as readonly string[]).includes(value)
}

export function isEquipmentOwnerType(value: string | null): value is EquipmentOwnerType {
  return value !== null && (EQUIPMENT_OWNER_TYPES as readonly string[]).includes(value)
}

/** Har tur uchun qaysi huquq kerak — ro'yxat so'ralishidan OLDIN tekshiriladi */
const OWNER_PERMISSION: Record<EquipmentOwnerType, Permission | null> = {
  SUBSCRIBER: 'subscriber.read',
  AGENT: 'agent.read',
  VENUE: 'venue.read',
  OTHER: null,
}

export interface OwnerOption {
  id: string
  /** Ekranda ko'rinadigan nom */
  label: string
  /** Ikkinchi darajali belgi (login, kod) — bir xil nomli ikkitasini ajratadi */
  hint: string | null
}

// --- Tor sxemalar: faqat id va nom ------------------------------------------

const subscriberPickSchema = z.object({
  rows: z.array(
    z.object({
      subscriber: z.object({
        id: uuidSchema,
        login: z.string(),
        displayName: z.string().nullish(),
      }),
    }),
  ),
})

/*
  ⚠️ Ikkalasi ham SAHIFA konvertida keladi (2026-08-20). Ilgari bu yerda
  `z.array(...)` turardi va endpointlar shaklni o'zgartirganda tanlagich
  jimgina yiqilardi — jadval emas, TANLOV bo'sh chiqardi va sabab hech
  qayerda ko'rinmasdi.
*/
const agentPickSchema = serverPageSchema(
  z.object({ id: uuidSchema, code: z.string(), displayName: z.string() }),
)

const venuePickSchema = serverPageSchema(
  z.object({ id: uuidSchema, code: z.string(), name: z.string() }),
)

/** Bitta so'rovda nechta variant ko'rsatiladi — tanlagich ro'yxati, jadval emas */
const LIMIT = 20

async function searchOwners(
  type: EquipmentOwnerType,
  q: string,
  signal: AbortSignal,
): Promise<readonly OwnerOption[]> {
  if (type === 'SUBSCRIBER') {
    const response = await api.get<unknown>('/api/subscribers', {
      signal,
      params: { page: 0, size: LIMIT, ...(q ? { search: q } : {}) },
    })
    return subscriberPickSchema.parse(response.data).rows.map(({ subscriber }) => ({
      id: subscriber.id,
      label: subscriber.displayName?.trim() || subscriber.login,
      hint: subscriber.displayName?.trim() ? subscriber.login : null,
    }))
  }

  if (type === 'AGENT') {
    /*
      ⚠️ `GET /api/agents` MAJBURIY `q` talab qiladi (`agentApi.searchAgents`
      izohi) — bo'sh so'z bilan so'rov 401 qaytaradi. Shuning uchun operator
      hech nima yozmaguncha ro'yxat BO'SH bo'ladi va tanlagich buni ochiq
      aytadi ("qidirish uchun yozing"), jimgina bo'sh ro'yxat ko'rsatmaydi.
    */
    if (q.length === 0) return []
    const response = await api.get<unknown>('/api/agents', {
      signal,
      params: { q, page: 0, size: LIMIT },
    })
    return agentPickSchema
      .parse(response.data)
      .rows.map((a) => ({ id: a.id, label: a.displayName, hint: a.code }))
  }

  if (type === 'VENUE') {
    /*
      ⚠️ Qidiruv endi SERVERDA (`search`). Ilgari endpoint butun ro'yxatni
      berardi va filtr mijozda edi; endi u sahifalangan, ya'ni mijozdagi
      filtr faqat BIRINCHI sahifani ko'rardi va operatorga mavjud ob'ektni
      "yo'q" deb ko'rsatardi.
    */
    const response = await api.get<unknown>('/api/venues', {
      signal,
      params: { page: 0, size: LIMIT, ...(q ? { search: q } : {}) },
    })
    return venuePickSchema
      .parse(response.data)
      .rows.map((v) => ({ id: v.id, label: v.name, hint: v.code }))
  }

  return []
}

export interface OwnerCandidates {
  options: readonly OwnerOption[]
  loading: boolean
  /** Bu tur uchun ro'yxat umuman bormi (`OTHER` da yo'q) */
  hasList: boolean
  /** Ro'yxatni ko'rish huquqi bormi */
  allowed: boolean
  /** Ro'yxat kelishi uchun avval qidiruv so'zi kerakmi (agentlar) */
  needsQuery: boolean
}

/**
 * Tanlangan tur bo'yicha egalar ro'yxati.
 *
 * ⚠️ Uch holat ALOHIDA ajratiladi va ularning HECH BIRI "bo'sh ro'yxat"
 * bo'lib ko'rinmaydi: huquq yo'q, qidiruv so'zi kerak, va haqiqatan
 * topilmadi. Ularni bittaga qo'shish operatorni "bu yerda hech kim yo'q"
 * degan YOLG'ON xulosaga olib borardi.
 */
export function useOwnerCandidates(
  type: EquipmentOwnerType,
  search: string,
  enabled: boolean,
): OwnerCandidates {
  const { can } = usePermissions()
  const permission = OWNER_PERMISSION[type]
  const allowed = permission === null || can(permission)
  const hasList = type !== 'OTHER'
  const q = search.trim()
  const needsQuery = type === 'AGENT' && q.length === 0

  const query = useQuery({
    queryKey: queryKeys.inventory.ownerSearch(type, q),
    queryFn: ({ signal }) => searchOwners(type, q, signal),
    enabled: enabled && hasList && allowed && !needsQuery,
    // Ro'yxat tanlash uchun — sessiya davomida deyarli o'zgarmaydi
    staleTime: 60_000,
  })

  return {
    options: query.data ?? [],
    loading: query.isFetching,
    hasList,
    allowed,
    needsQuery,
  }
}

/**
 * Bitta eganing NOMI — jadval va kartochkada xom UUID o'rniga.
 *
 * ⚠️ `AgentRef` bilan bir xil uch holatli qoida: huquq yo'q — qisqartirilgan
 * id (egasi BOR, shunchaki bizga ko'rinmaydi), topilmadi — sariq
 * ogohlantirish, `OTHER` — id o'z holicha. Bo'sh katak ularning hammasini
 * bir xil ko'rsatardi.
 */
export interface OwnerLabel {
  name: string | null
  hint: string | null
  loading: boolean
  allowed: boolean
  /** So'rov yiqildi yoki yozuv topilmadi */
  missing: boolean
  /** Bu tur uchun nom umuman izlanmaydi (`OTHER`) */
  resolvable: boolean
}

const subscriberOneSchema = z.object({
  id: uuidSchema,
  login: z.string(),
  displayName: z.string().nullish(),
})
const agentOneSchema = z.object({ id: uuidSchema, code: z.string(), displayName: z.string() })
const venueOneSchema = z.object({ id: uuidSchema, code: z.string(), name: z.string() })

async function fetchOwner(type: EquipmentOwnerType, id: string): Promise<OwnerOption> {
  if (type === 'SUBSCRIBER') {
    const r = await api.get<unknown>(`/api/subscribers/${id}`)
    const s = subscriberOneSchema.parse(r.data)
    return { id: s.id, label: s.displayName?.trim() || s.login, hint: s.login }
  }
  if (type === 'AGENT') {
    const r = await api.get<unknown>(`/api/agents/${id}`)
    const a = agentOneSchema.parse(r.data)
    return { id: a.id, label: a.displayName, hint: a.code }
  }
  const r = await api.get<unknown>(`/api/venues/${id}`)
  const v = venueOneSchema.parse(r.data)
  return { id: v.id, label: v.name, hint: v.code }
}

export function useOwnerLabel(ownerType: string | null, ownerId: string | null): OwnerLabel {
  const { can } = usePermissions()
  const type = isEquipmentOwnerType(ownerType) ? ownerType : null
  const resolvable = type !== null && type !== 'OTHER'
  const permission = type === null ? null : OWNER_PERMISSION[type]
  const allowed = permission === null || can(permission)

  const query = useQuery({
    queryKey: queryKeys.inventory.owner(ownerType ?? '', ownerId ?? ''),
    queryFn: () => fetchOwner(type as Exclude<EquipmentOwnerType, 'OTHER'>, ownerId as string),
    enabled: resolvable && allowed && ownerId !== null,
    staleTime: 5 * 60_000,
    // O'chirilgan egaga havola 404 beradi — qayta urinish uni tuzatmaydi,
    // faqat jurnalni shovqinga to'ldiradi (`AgentRef` bilan bir xil sabab)
    retry: false,
  })

  return useMemo(
    () => ({
      name: query.data?.label ?? null,
      hint: query.data?.hint ?? null,
      loading: resolvable && allowed && ownerId !== null && query.isPending,
      allowed,
      missing: query.isError,
      resolvable,
    }),
    [query.data, query.isPending, query.isError, resolvable, allowed, ownerId],
  )
}
